import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IdcardOutlined, MessageOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Modal, Popover } from 'antd';
import { useServerStore, useAgentStore, useAuthStore } from '../stores';
import { addGuildMember, fetchAgents, type Agent } from '../lib/api';
import InviteLinksModal from './InviteLinksModal';
import { UserBadge } from './UserBadge';
import { DISABLED_LABEL } from '../lib/agent-state';
import ServerMemberRemovalControl from './ServerMemberRemovalControl';
import MemberProjectProfilePopover from './MemberProjectProfilePopover';
import surface from './ProjectSurface.module.css';
import styles from './ProjectMembersPage.module.css';
import ProjectPageHeader from './ProjectPageHeader';

interface Props {
  selectedMemberId: string | null;
  onSelectMember: (id: string) => void;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

interface AddOwnedAgentDialogProps {
  guildId: string;
  currentUserId: string;
  memberIds: Set<string>;
  onClose: () => void;
}

function AddOwnedAgentDialog({ guildId, currentUserId, memberIds, onClose }: AddOwnedAgentDialogProps) {
  const loadMembers = useServerStore(s => s.loadMembers);
  const [initialMemberIds] = useState(() => new Set(memberIds));
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [refreshError, setRefreshError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const generation = useRef(0);
  const submissionInFlight = useRef(false);

  const loadCandidates = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    setLoadError('');
    setSubmitError('');
    setRefreshError('');
    setSelectedId(null);
    setAgents([]);
    try {
      const freshAgents = await fetchAgents();
      if (request !== generation.current) return;
      setAgents(freshAgents.filter(agent => agent.owner_id === currentUserId && !agent.disabled && !initialMemberIds.has(agent.id)));
    } catch (error) {
      if (request !== generation.current) return;
      setLoadError(errorMessage(error, 'Could not load your agents.'));
    } finally {
      if (request === generation.current) setLoading(false);
    }
  }, [currentUserId, initialMemberIds]);

  useEffect(() => {
    void loadCandidates();
    return () => {
      generation.current++;
    };
  }, [loadCandidates]);

  const refreshRoster = async (request = ++generation.current) => {
    setRefreshError('');
    try {
      const result = await loadMembers(guildId);
      if (request !== generation.current) return;
      if (result.status === 'applied') {
        onClose();
        return;
      }
      setRefreshError('The Project member list refresh was superseded. Retry refresh to confirm the updated roster.');
    } catch (error) {
      if (request === generation.current) setRefreshError(errorMessage(error, 'The agent was added, but the Project member list could not be refreshed.'));
    }
  };

  const handleCancel = () => {
    if (submitting || submissionInFlight.current) return;
    onClose();
  };

  const submit = async () => {
    // React state updates are asynchronous, so use a ref to reject two clicks
    // that occur before the button has re-rendered as disabled.
    if (!selectedId || submitting || submissionInFlight.current || refreshError) return;
    submissionInFlight.current = true;
    const request = ++generation.current;
    setSubmitting(true);
    setSubmitError('');
    try {
      await addGuildMember(guildId, selectedId);
      if (request !== generation.current) return;
      await refreshRoster(request);
    } catch (error) {
      if (request === generation.current) setSubmitError(errorMessage(error, 'Could not add the agent to this Project.'));
    } finally {
      submissionInFlight.current = false;
      if (request === generation.current) setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      title="Add Agent"
      onCancel={handleCancel}
      closable={!submitting}
      keyboard={!submitting}
      maskClosable={!submitting}
      footer={null}
      destroyOnHidden
    >
      <p id="add-owned-agent-description">Choose one of your active Agents to add to this Project.</p>
      {loading && <p role="status">Loading your Agents…</p>}
      {loadError && (
        <Alert
          type="error"
          showIcon
          message="Could not load your Agents"
          description={loadError}
          action={<Button size="small" onClick={() => void loadCandidates()}>Retry</Button>}
        />
      )}
      {submitError && <Alert type="error" showIcon message="Could not add the Agent" description={submitError} />}
      {refreshError && (
        <Alert
          type="warning"
          showIcon
          message="Agent added, but the Project member list could not be refreshed"
          description={refreshError}
          action={<Button size="small" onClick={() => void refreshRoster()}>Retry refresh</Button>}
        />
      )}
      {!loading && !loadError && !refreshError && agents.length === 0 && <p>No eligible Agents are available.</p>}
      {!loading && !loadError && !refreshError && agents.length > 0 && (
        <div role="radiogroup" aria-label="Your active Agents" aria-describedby="add-owned-agent-description">
          {agents.map((agent, index) => (
            <label key={agent.id} style={{ display: 'block', marginBottom: 8 }}>
              <input
                type="radio"
                name="owned-agent"
                value={agent.id}
                autoFocus={index === 0}
                checked={selectedId === agent.id}
                onChange={() => setSelectedId(agent.id)}
                disabled={submitting}
              />{' '}
              {agent.display_name}
            </label>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button onClick={handleCancel} disabled={submitting}>Cancel</Button>
        <Button type="primary" onClick={() => void submit()} disabled={!selectedId || submitting || !!refreshError} loading={submitting}>Add Agent</Button>
      </div>
    </Modal>
  );
}

export default function ProjectMembersPage({ selectedMemberId, onSelectMember }: Props) {
  const members = useServerStore(s => s.members);
  const loaded = useServerStore(s => s.loaded);
  const loadedGuildId = useServerStore(s => s.loadedGuildId);
  const guilds = useServerStore(s => s.guilds);
  const guildsLoaded = useServerStore(s => s.guildsLoaded);
  const agents = useAgentStore(s => s.agents);
  const currentUser = useAuthStore(s => s.currentUser);
  const guildId = useAuthStore(s => s.guildId);
  const [query, setQuery] = useState('');
  const [role, setRole] = useState<'all' | 'member' | 'agent'>('all');
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [addExistingDialog, setAddExistingDialog] = useState<{ guildId: string; currentUserId: string; memberIds: Set<string> } | null>(null);
  const [showInvites, setShowInvites] = useState(false);
  const [profileMemberId, setProfileMemberId] = useState<string | null>(null);
  const isOwner = guildsLoaded && !!guildId && guilds.find(guild => guild.id === guildId)?.owner_id === currentUser?.id;
  const hasCurrentRoster = loaded && loadedGuildId === guildId && !!currentUser && members.some(member => member.user_id === currentUser.id);
  const canManageAgents = !!isOwner && hasCurrentRoster && !!currentUser;
  const people = useMemo(() => members.filter(member => {
    const matchesRole = role === 'all' || member.role === role;
    return matchesRole && member.display_name.toLowerCase().includes(query.trim().toLowerCase());
  }), [members, query, role]);

  useEffect(() => {
    if (!addExistingDialog) return;
    const sameSession = guildId === addExistingDialog.guildId && currentUser?.id === addExistingDialog.currentUserId;
    if (!sameSession || (loaded && !canManageAgents)) setAddExistingDialog(null);
  }, [addExistingDialog, canManageAgents, currentUser?.id, guildId, loaded]);

  const openInvite = () => { setShowAddMenu(false); setShowInvites(true); };
  const openAgentPicker = () => {
    if (!canManageAgents || !guildId || !currentUser) return;
    setShowAddMenu(false);
    setAddExistingDialog({ guildId, currentUserId: currentUser.id, memberIds: new Set(members.map(member => member.user_id)) });
  };
  return <main className={surface.pageRoot} aria-label="Project members">
    <ProjectPageHeader title="Members" end={isOwner && guildId ? <div className={styles.addMenu}><Button type="primary" icon={<PlusOutlined />} aria-label="Add member" onClick={() => setShowAddMenu(open => !open)}>Add member</Button>{showAddMenu && <div role="menu" aria-label="Add member options"><button type="button" role="menuitem" onClick={openInvite}>Invite human</button>{canManageAgents && <button type="button" role="menuitem" onClick={openAgentPicker}>Add Agent</button>}</div>}</div> : null} />
    <section className={`${surface.pageContainer} ${styles.people}`}><header><div><h2>People</h2><span>{people.length} visible members</span></div></header><div className={styles.filters}><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search people…" aria-label="Search members" />{(['all', 'member', 'agent'] as const).map(value => <button type="button" key={value} onClick={() => setRole(value)} className={role === value ? styles.filterActive : ''}>{value === 'all' ? 'All' : value === 'agent' ? 'Agents' : 'Humans'}</button>)}</div><div className={surface.borderedList}>{people.map(member => { const agent = agents.find(item => item.id === member.user_id); const profileOpen = profileMemberId === member.user_id; return <div key={member.user_id} className={styles.rowWrap}><button type="button" className={`${surface.listRow} ${surface.listRowInteractive} ${styles.row} channel-item${selectedMemberId === member.user_id ? ` ${surface.listRowActive}` : ''}${agent?.disabled ? ' channel-item-disabled' : ''}`} onClick={() => onSelectMember(member.user_id)} aria-label={`Open direct message with ${member.display_name}`}><UserBadge userId={member.user_id} displayName={member.display_name} role={member.role} avatarUrl={member.avatar_url} state={agent?.state ?? member.state} disabled={agent?.disabled} size={30} />{member.role === 'agent' && agent?.disabled && <span className="agent-disabled-badge">{DISABLED_LABEL}</span>}</button><div className={styles.rowActions}><Popover trigger="click" placement="bottomRight" autoAdjustOverflow destroyOnHidden getPopupContainer={() => document.body} classNames={{ root: styles.profilePopover }} open={profileOpen} onOpenChange={open => setProfileMemberId(open ? member.user_id : null)} content={<MemberProjectProfilePopover guildId={guildId!} member={member} />}><button type="button" className="icon-btn" aria-label={`View ${member.display_name} profile`} title="View Project profile"><IdcardOutlined /></button></Popover>{member.user_id !== currentUser?.id && <button type="button" className="icon-btn" aria-label={`Open direct message with ${member.display_name}`} title="Open direct message" onClick={() => onSelectMember(member.user_id)}><MessageOutlined /></button>}<ServerMemberRemovalControl userId={member.user_id} displayName={member.display_name} role={member.role === 'agent' ? 'agent' : 'member'} compact /></div></div>; })}{!people.length && <p className={surface.listEmpty}>No matching members.</p>}</div></section>
    {addExistingDialog && guildId === addExistingDialog.guildId && currentUser?.id === addExistingDialog.currentUserId && <AddOwnedAgentDialog guildId={addExistingDialog.guildId} currentUserId={addExistingDialog.currentUserId} memberIds={addExistingDialog.memberIds} onClose={() => setAddExistingDialog(null)} />}
    {showInvites && guildId && <InviteLinksModal guildId={guildId} onClose={() => setShowInvites(false)} />}
  </main>;
}
